/**
 * Migros Yemek'in İKİ ayrı statü sözlüğünü tek bir kanonik değere indirger.
 *
 *   - Yazma sözlüğü (`Mapping/GetOrderStatusList`, `Order/v2/UpdateOrderStatus`):
 *     Approved, Rejected, Prepared, Delivery, Completed
 *   - Okuma sözlüğü (`Order/GetSummarizedOrder`, sipariş listeleri, webhook):
 *     NEW_PENDING, COLLECTING, COLLECTION_APPROVED, IN_DELIVERY, DELIVERED, CANCELLED
 *
 * Onay isteğinden sonra veritabanına yazma sözlüğü ("Approved") yazılıyor, ancak
 * polling/webhook aynı siparişi okuma sözlüğüyle ("COLLECTING") güncelliyor. Yani
 * tek bir ekranda iki sözlük birlikte dolaşıyor ve her tüketici ikisini de tanımak
 * zorunda; bu yüzden eşleme tek bir yerde tutuluyor.
 *
 * Bilinmeyen değer ASLA "onay bekliyor" sayılmamalı: eşlenmeyen statü 'new' kabul
 * edildiğinde onaylanmış siparişler tekrar yeni sipariş gibi görünüyor, kasiyer
 * yeniden onaylamayı deniyor ve Migros bu isteği reddediyordu.
 */
export type MigrosCanonicalStatus =
  | 'NEW_PENDING'
  | 'APPROVED'
  | 'PREPARED'
  | 'DELIVERY'
  | 'COMPLETED'
  | 'REJECTED'
  | 'CANCELLED'
  | 'UNKNOWN';

/** Her iki sözlükteki değerler ve kodda öteden beri karşılaşılan varyantlar. */
const STATUS_ALIASES: Record<string, MigrosCanonicalStatus> = {
  // Onay bekliyor
  NEW_PENDING: 'NEW_PENDING',
  PENDING: 'NEW_PENDING',

  // Onaylandı — okuma sözlüğünde sipariş "hazırlanıyor" olarak görünür
  APPROVED: 'APPROVED',
  COLLECTING: 'APPROVED',

  // Hazırlandı
  PREPARED: 'PREPARED',
  COLLECTION_APPROVED: 'PREPARED',

  // Kuryede / yolda
  DELIVERY: 'DELIVERY',
  IN_DELIVERY: 'DELIVERY',

  // Müşteriye teslim edildi
  COMPLETED: 'COMPLETED',
  DELIVERED: 'COMPLETED',

  // Reddedildi / iptal edildi
  REJECTED: 'REJECTED',
  CANCELLED: 'CANCELLED',
};

/**
 * Migros'tan gelen ham statüyü kanonik değere çevirir. Tanınmayan değer 'UNKNOWN'
 * döner ve konsola yazılır; çağıran taraf bu durumda işlem butonu göstermemeli.
 */
export const normalizeMigrosStatus = (raw?: string | null): MigrosCanonicalStatus => {
  const key = (raw || '').trim().toUpperCase().replace(/[\s-]+/g, '_');
  if (!key) return 'UNKNOWN';

  const mapped = STATUS_ALIASES[key];
  if (mapped) return mapped;

  console.warn('[Migros] Eşlenmeyen sipariş statüsü:', raw);
  return 'UNKNOWN';
};

/** Yeni sipariş modalı / ses yalnızca onay bekleyen sipariş için. */
export function isMigrosNewOrderNotifiableStatus(raw?: string | null): boolean {
  return normalizeMigrosStatus(raw) === 'NEW_PENDING';
}
